import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CommandIcon from './CommandIcon.jsx';
import { MAX_TEXT_LENGTH, sendTextMessage, textMessageErrorKey } from '../lib/messages.js';

export default function TextMessenger({ credentials, ended, onOpenChange, t }) {
  const [open, setOpen] = useState(false), [phase, setPhase] = useState('draft');
  const [text, setText] = useState(''), [senderName, setSenderName] = useState(''), [errorKey, setErrorKey] = useState('');
  const launchRef = useRef(null), dialogRef = useRef(null), textRef = useRef(null), messageIdRef = useRef('');
  const controllerRef = useRef(null), mountedRef = useRef(true);
  const sending = phase === 'sending';
  const close = useCallback(() => {
    if (sending) return;
    setOpen(false); onOpenChange(false); setText(''); setErrorKey(''); setPhase('draft');
    requestAnimationFrame(() => launchRef.current?.focus());
  }, [sending, onOpenChange]);
  const show = () => {
    try { setSenderName(localStorage.getItem('cynoops.live.voiceName') || ''); } catch {}
    messageIdRef.current = crypto.randomUUID(); setText(''); setErrorKey(''); setPhase('draft'); setOpen(true); onOpenChange(true);
  };
  const edit = (setter, value) => { setter(value); messageIdRef.current = crypto.randomUUID(); setErrorKey(''); };
  const send = async event => {
    event.preventDefault();
    if (sending || !text.trim() || text.length > MAX_TEXT_LENGTH || ended) return;
    setPhase('sending'); setErrorKey('');
    const controller = new AbortController(); controllerRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      await sendTextMessage({ credentials, messageId: messageIdRef.current, text, senderName, signal: controller.signal });
      if (!mountedRef.current) return;
      try { localStorage.setItem('cynoops.live.voiceName', senderName.trim()); } catch {}
      setText(''); setPhase('sent');
    } catch (error) { if (mountedRef.current) { setErrorKey(textMessageErrorKey(error)); setPhase('draft'); } }
    finally { clearTimeout(timeout); controllerRef.current = null; }
  };
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; controllerRef.current?.abort(); };
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    if (phase === 'draft') textRef.current?.focus();
    if (phase === 'sent') dialogRef.current?.querySelector('[data-text-done]')?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key !== 'Tab') return;
      const controls = [...(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), textarea:not(:disabled)') || [])];
      if (!controls.length) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === controls.at(-1)) { event.preventDefault(); controls[0].focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, phase, close]);

  return <>
    <button ref={launchRef} type="button" className="live-text-launcher" data-testid="live-text-button" disabled={ended}
      onClick={show} title={ended ? t('voice.sessionEnded') : t('text.title')} aria-label={t('text.title')}>
      <CommandIcon name="chat-text" /><span>{t('text.button')}</span>
    </button>
    {open && createPortal(<div className="voice-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="text-title" className="voice-dialog text-message-dialog" data-testid="text-dialog">
        <header className="settings-dialog__header"><CommandIcon name="chat-text" /><h2 id="text-title">{t('text.title')}</h2>
          <button type="button" className="icon-button" disabled={sending} onClick={close} aria-label={t('text.close')}><CommandIcon name="x" /></button>
        </header>
        <form onSubmit={send}>
          <div className="voice-dialog__body">
            {phase === 'sent' ? <div className="voice-sent" role="status"><CommandIcon name="check-circle" /><h3>{t('voice.sent')}</h3><p>{t('text.sentBody')}</p></div> : <div className="voice-review">
              <label htmlFor="live-message-text">{t('text.message')}</label>
              <textarea ref={textRef} id="live-message-text" rows={6} maxLength={MAX_TEXT_LENGTH} value={text} disabled={sending}
                onChange={event => edit(setText, event.target.value)} aria-describedby="text-character-count" />
              <span id="text-character-count" className="text-character-count">{text.length} / {MAX_TEXT_LENGTH}</span>
              <label htmlFor="live-message-sender">{t('voice.name')}</label>
              <input id="live-message-sender" value={senderName} maxLength={80} disabled={sending} onChange={event => edit(setSenderName, event.target.value)} autoComplete="name" />
            </div>}
            {errorKey && <p className="inline-error" role="alert">{t(errorKey)}</p>}
            {ended && phase !== 'sent' && <p className="inline-error" role="alert">{t('voice.sessionEnded')}</p>}
          </div>
          <footer className="settings-dialog__footer">
            {phase === 'sent' ? <button type="button" className="button button--primary" data-text-done onClick={close}>{t('settings.done')}</button> : <>
              <button type="button" className="button" disabled={sending} onClick={close}>{t('voice.discard')}</button>
              <button type="submit" className="button button--primary" disabled={sending || ended || !text.trim() || text.length > MAX_TEXT_LENGTH} data-testid="text-send">
                {sending ? <span className="spinner" /> : <CommandIcon name="paper-plane-tilt" />}{t(sending ? 'voice.sending' : 'voice.send')}
              </button>
            </>}
          </footer>
        </form>
      </section>
    </div>, document.body)}
  </>;
}
